/**
 * Modo global de visualização de valores financeiros (profiles.visualizarValores).
 * 0 = valores reais | 1 = tudo zerado | 2 = valores simulados.
 *
 * O modo é guardado num módulo global para que TODA formatação monetária do app
 * (inclusive telas que usam os formatadores puros brl/brl3/brl4) respeite a
 * configuração do usuário, mesmo que ele seja administrador.
 */
export type ModoValores = 0 | 1 | 2;

let modoAtual: ModoValores = 0;

const ouvintes = new Set<() => void>();

/** Assina mudanças do modo (para useSyncExternalStore). */
export function assinarModoValores(fn: () => void) {
  ouvintes.add(fn);
  return () => ouvintes.delete(fn);
}

export function definirModoValores(modo: ModoValores) {
  if (modoAtual === modo) return;
  modoAtual = modo;
  ouvintes.forEach((fn) => fn());
}

export function obterModoValores(): ModoValores {
  return modoAtual;
}

/** Limites (em %) da simulação de valores: padrão 50% a 200% do valor real. */
let limiteMin = 50;
let limiteMax = 200;

export function definirLimitesSimulacao(min: number, max: number) {
  const novoMin = Number.isFinite(Number(min)) ? Number(min) : 50;
  const novoMax = Number.isFinite(Number(max)) ? Number(max) : 200;
  if (novoMin === limiteMin && novoMax === limiteMax) return;
  limiteMin = novoMin;
  limiteMax = novoMax;
  ouvintes.forEach((fn) => fn());
}

export function obterLimitesSimulacao() {
  return { min: limiteMin, max: limiteMax };
}

/**
 * Semente da simulação: muda a cada carga de aba, de modo que os valores
 * simulados sejam diferentes a cada navegação (mas estáveis dentro da tela).
 */
// Start deterministic to avoid SSR/client hydration mismatches; randomized
// seed is applied on client mount via `renovarSementeSimulacao`.
let semente = 0;

/** Sorteia uma nova semente e avisa quem estiver exibindo valores. */
export function renovarSementeSimulacao() {
  semente = Math.floor(Math.random() * 2 ** 31);
  ouvintes.forEach((fn) => fn());
}

/** Embaralhador: o mesmo valor gera o mesmo número dentro da mesma carga. */
function simular(v: number) {
  const chave = Math.round(Math.abs(v) * 1000);
  let h = 2166136261 ^ chave ^ semente;
  h = Math.imul(h ^ (h >>> 15), 2246822507);
  h = Math.imul(h ^ (h >>> 13), 3266489909);
  const r = ((h >>> 0) % 100000) / 100000; // 0 .. 1
  const min = limiteMin / 100;
  const max = limiteMax / 100;
  return v * (min + r * (max - min));
}

/** Aplica um modo específico a um número lido do banco. */
export function aplicarModoValor(v: number, modo: ModoValores) {
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return 0;
  if (modo === 1) return 0;
  if (modo === 2) return simular(n);
  return n;
}

/** Aplica o modo global vigente a um número lido do banco. */
export function mascararValor(v: number) {
  return aplicarModoValor(Number(v), modoAtual);
}

/**
 * Quantidades (itens vendidos, consumo de insumo): ficam zeradas para quem não
 * pode ver nada (modo 1). No modo simulado (2) as quantidades reais continuam.
 */
export function mascararQuantidade(v: number) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return modoAtual === 1 ? 0 : n;
}
